import { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';
import { Cog, Plus, Pencil, Trash2, Box } from 'lucide-react';

export default function MachinesPage() {
  const [machines, setMachines] = useState([]);
  const [ilots, setIlots] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [formData, setFormData] = useState({ name: '', type: '', ilot_id: '', row_color: '' });

  // Available colors for machine rows in planning
  const availableColors = [
    { value: 'none', label: 'Aucune couleur', color: 'bg-gray-100' },
    { value: 'blue', label: 'Bleu', color: 'bg-blue-100' },
    { value: 'green', label: 'Vert', color: 'bg-green-100' },
    { value: 'yellow', label: 'Jaune', color: 'bg-yellow-100' },
    { value: 'red', label: 'Rouge', color: 'bg-red-100' },
    { value: 'purple', label: 'Violet', color: 'bg-purple-100' },
    { value: 'pink', label: 'Rose', color: 'bg-pink-100' },
    { value: 'orange', label: 'Orange', color: 'bg-orange-100' },
    { value: 'cyan', label: 'Cyan', color: 'bg-cyan-100' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [machinesRes, ilotsRes, typesRes] = await Promise.all([
        fetch(`${API}/machines`, { credentials: 'include' }),
        fetch(`${API}/ilots`, { credentials: 'include' }),
        fetch(`${API}/machine-types/list/all`, { credentials: 'include' })
      ]);
      
      if (machinesRes.ok && ilotsRes.ok) {
        const machinesData = await machinesRes.json();
        const ilotsData = await ilotsRes.json();
        setMachines(machinesData);
        setIlots(ilotsData);
        
        // Load machine types
        if (typesRes.ok) {
          const typesData = await typesRes.json();
          setMachineTypes(typesData);
        } else {
          // Fallback to empty array if no types available
          setMachineTypes([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    if (ilots.length === 0) {
      toast.error('Créez d\'abord un îlot');
      return;
    }
    if (machineTypes.length === 0) {
      toast.error('Créez d\'abord des types de machines dans Administration');
      return;
    }
    setEditingMachine(null);
    setFormData({ 
      name: '', 
      type: machineTypes[0]?.name || '', 
      ilot_id: ilots[0].ilot_id,
      row_color: 'none'
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (machine) => {
    setEditingMachine(machine);
    setFormData({ 
      name: machine.name, 
      type: machine.type, 
      ilot_id: machine.ilot_id,
      row_color: machine.row_color || 'none'
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingMachine ? `${API}/machines/${editingMachine.machine_id}` : `${API}/machines`;
      const method = editingMachine ? 'PUT' : 'POST';
      
      // Convert 'none' to empty string for backend
      const dataToSend = {
        ...formData,
        row_color: formData.row_color === 'none' ? '' : formData.row_color
      };
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        toast.success(editingMachine ? 'Machine mise à jour' : 'Machine créée');
        setDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleDelete = async (machine) => {
    if (!window.confirm(`Supprimer la machine "${machine.name}" ?`)) return;
    
    try {
      const response = await fetch(`${API}/machines/${machine.machine_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Machine supprimée');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getIlotName = (ilotId) => {
    const ilot = ilots.find(i => i.ilot_id === ilotId);
    return ilot?.name || 'N/A';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Tournage': 'bg-blue-500',
      'Fraisage': 'bg-green-500',
      'Perçage': 'bg-purple-500',
      'Rectification': 'bg-pink-500',
      'Usinage': 'bg-orange-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="machines-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="machines-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight uppercase">
        Gestion des Machines
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-sm uppercase tracking-wider">
            Configurez vos machines-outils
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="btn-industrial rounded-sm" data-testid="create-machine-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle Machine
        </Button>
      </div>

      {/* Machines Table */}
      <Card className="border-2 rounded-sm">
        <CardContent className="p-0">
          {machines.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Cog className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
              <h3 className="font-heading font-semibold text-xl uppercase tracking-tight mb-2">
                Aucune machine
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                {ilots.length === 0 
                  ? 'Créez d\'abord un îlot pour ajouter des machines'
                  : 'Ajoutez votre première machine-outil'
                }
              </p>
              {ilots.length > 0 && (
                <Button onClick={handleOpenCreate} className="btn-industrial rounded-sm" data-testid="create-first-machine-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une machine
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b-2">
                  <TableHead className="industrial-label">Nom</TableHead>
                  <TableHead className="industrial-label">Type</TableHead>
                  <TableHead className="industrial-label">Îlot</TableHead>
                  <TableHead className="industrial-label">Couleur Planning</TableHead>
                  <TableHead className="industrial-label">Date de création</TableHead>
                  <TableHead className="industrial-label text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {machines.map((machine) => {
                  // Find the type object to get category for color
                  const typeObj = machineTypes.find(t => t.name === machine.type);
                  const categoryColor = typeObj ? getCategoryColor(typeObj.category) : 'bg-gray-500';
                  
                  // Get planning color
                  const planningColor = availableColors.find(c => c.value === machine.row_color);
                  
                  return (
                    <TableRow key={machine.machine_id} className="border-b" data-testid={`machine-row-${machine.machine_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center">
                            <Cog className="w-4 h-4 text-foreground" strokeWidth={1.5} />
                          </div>
                          <span className="font-medium">{machine.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1.5 font-mono text-xs">
                          <div className={`w-2 h-2 rounded-full ${categoryColor}`} />
                          {machine.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Box className="w-4 h-4 text-muted-foreground" />
                          <span className="font-mono text-sm">{getIlotName(machine.ilot_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-4 rounded border ${planningColor?.color || 'bg-gray-100'} ${(!machine.row_color || machine.row_color === 'none') && 'border-dashed'}`} />
                          <span className="text-sm text-muted-foreground">{planningColor?.label || 'Aucune'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {new Date(machine.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(machine)}
                            data-testid={`edit-machine-${machine.machine_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(machine)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-machine-${machine.machine_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm border-2">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-xl uppercase tracking-tight">
              {editingMachine ? 'Modifier la machine' : 'Nouvelle machine'}
            </DialogTitle>
            <DialogDescription className="font-mono text-xs uppercase tracking-wider">
              {editingMachine ? 'Modifiez les informations de la machine' : 'Ajoutez une nouvelle machine-outil'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="machine-name" className="industrial-label">Nom de la machine</Label>
                <Input
                  id="machine-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="CNC-001"
                  className="rounded-sm border-2"
                  required
                  data-testid="machine-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="industrial-label">Type de machine</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className="rounded-sm border-2" data-testid="machine-type-select">
                    <SelectValue placeholder="Sélectionnez un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {machineTypes.length === 0 ? (
                      <div className="px-2 py-4 text-sm text-center text-muted-foreground">
                        Aucun type disponible.<br />
                        Créez-en dans Administration.
                      </div>
                    ) : (
                      machineTypes.map((type) => (
                        <SelectItem key={type.type_id} value={type.name}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${getCategoryColor(type.category)}`} />
                            <span>{type.name}</span>
                            {type.category && (
                              <span className="text-xs text-muted-foreground">({type.category})</span>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="industrial-label">Îlot</Label>
                <Select
                  value={formData.ilot_id}
                  onValueChange={(value) => setFormData({ ...formData, ilot_id: value })}
                >
                  <SelectTrigger className="rounded-sm border-2" data-testid="machine-ilot-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ilots.map((ilot) => (
                      <SelectItem key={ilot.ilot_id} value={ilot.ilot_id}>
                        {ilot.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="industrial-label">Couleur dans le Planning</Label>
                <Select
                  value={formData.row_color}
                  onValueChange={(value) => setFormData({ ...formData, row_color: value })}
                >
                  <SelectTrigger className="rounded-sm border-2" data-testid="machine-color-select">
                    <SelectValue placeholder="Sélectionnez une couleur" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableColors.map((colorOption) => (
                      <SelectItem key={colorOption.value} value={colorOption.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded border ${colorOption.color} ${!colorOption.value && 'border-dashed'}`} />
                          <span>{colorOption.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Couleur de fond de la ligne dans la page Planning
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm border-2">
                Annuler
              </Button>
              <Button type="submit" className="btn-industrial rounded-sm" data-testid="save-machine-btn">
                {editingMachine ? 'Enregistrer' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
